import { Button, PageEmpty, PageError, PageHeader, PageLoading, TextButton } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { Input } from "@majitap/ui/arc/input/input";
import { SortableDataTable } from "@majitap/ui/arc/sortable-data-table/sortable-data-table";
import { Textarea } from "@majitap/ui/arc/textarea/textarea";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { errorOf } from "../../api";
import { useRecordCrumb } from "../../crumb";
import { messageFor } from "../../error-copy";
import { useFormCheck } from "../../form-check";
import {
  FULFILLMENT,
  PRODUCT_TYPE,
  type Product,
  type ProductItem,
  productActions,
  productError,
  productQuery,
  UNIT,
} from "../../products";
import { meQuery } from "../../session";
import { ConfirmDialog } from "../shared/confirm";
import { ActivityPanel } from "../shared/panels";
import { CategorySelect } from "./categories";
import { blankItem, ItemFields, itemErrors, toNewItem } from "./items";
import styles from "./products.module.css";

function useRefreshProduct(id: string) {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ["catalog"] });
    await queryClient.invalidateQueries({ queryKey: ["activity", "product", id] });
  };
}

/** What `catalog.manage` may do on a product's page. */
export const productActionsFor = (permissions: string[]) => ({ edit: permissions.includes("catalog.manage") });

/** One product (P8-18): its items, what can change, and its history. */
export function ProductPage(): ReactNode {
  const { productId } = useParams({ from: "/console/products/$productId" });
  const { data: me } = useSuspenseQuery(meQuery);
  const permissions = me?.permissions ?? [];
  const product = useQuery(productQuery(productId));
  useRecordCrumb(product.data?.name);
  if (product.status === "pending") return <PageLoading label="Loading the product" />;
  if (product.status === "error") {
    const error = errorOf(product.error);
    if (error.code === "PRODUCT_NOT_FOUND")
      return <PageEmpty title="No such product" description="It may be in another company, or the link is wrong." />;
    return <PageError message={messageFor(error)} onRetry={() => product.refetch()} />;
  }
  return <ProductView product={product.data} permissions={permissions} />;
}

type Dialogs =
  | { kind: "edit" }
  | { kind: "active" }
  | { kind: "add-item" }
  | { kind: "edit-item"; item: ProductItem }
  | { kind: "item-active"; item: ProductItem }
  | null;

function ProductView({ product: p, permissions }: { product: Product; permissions: string[] }) {
  const can = productActionsFor(permissions);
  const refresh = useRefreshProduct(p.id);
  const [dialog, setDialog] = useState<Dialogs>(null);
  const close = () => setDialog(null);
  return (
    <>
      <PageHeader
        title={p.name}
        description={[PRODUCT_TYPE[p.type], p.category?.name, p.is_active ? "on sale" : "inactive", p.description]
          .filter(Boolean)
          .join(" · ")}
        actions={
          can.edit ? (
            <div className={styles.actions}>
              <Button variant="secondary" onClick={() => setDialog({ kind: "active" })}>
                {p.is_active ? "Deactivate" : "Put back on sale"}
              </Button>
              <Button variant="secondary" onClick={() => setDialog({ kind: "edit" })}>
                Edit
              </Button>
            </div>
          ) : undefined
        }
      />
      {p.is_active ? null : (
        <Alert tone="info" title="This product is inactive">
          Customers can't order its items. Orders already placed go on, and its prices are kept.
        </Alert>
      )}
      <section className={styles.section}>
        <div className={styles.head}>
          <h2 className={styles.sectionTitle}>Items</h2>
          {can.edit ? (
            <Button variant="secondary" size="sm" onClick={() => setDialog({ kind: "add-item" })}>
              Add an item
            </Button>
          ) : null}
        </div>
        <SortableDataTable
          caption="Items"
          rowKey="id"
          rows={p.items}
          itemName={{ one: "item", other: "items" }}
          columns={[
            { key: "name", label: "Item", sortable: false },
            {
              key: "sku",
              label: "SKU",
              sortable: false,
              render: (_v, i) => <span className={styles.mono}>{i.sku}</span>,
            },
            {
              key: "unit_size",
              label: "Size",
              sortable: false,
              render: (_v, i) => `${i.unit_size} ${UNIT[i.unit].toLowerCase()}`,
            },
            {
              key: "fulfillment",
              label: "Fulfilment",
              sortable: false,
              render: (_v, i) => FULFILLMENT[i.fulfillment].label,
            },
            {
              key: "is_active",
              label: "Status",
              sortable: false,
              render: (_v, i) => (
                <Badge tone={i.is_active ? "success" : "neutral"}>{i.is_active ? "On sale" : "Inactive"}</Badge>
              ),
            },
            {
              key: "actions",
              label: "",
              sortable: false,
              render: (_v, i) =>
                can.edit ? (
                  <span className={styles.actions}>
                    <TextButton onClick={() => setDialog({ kind: "edit-item", item: i })}>Edit</TextButton>
                    <TextButton onClick={() => setDialog({ kind: "item-active", item: i })}>
                      {i.is_active ? "Deactivate" : "Put back on sale"}
                    </TextButton>
                  </span>
                ) : null,
            },
          ]}
        />
        <p className={styles.muted}>
          An item's unit, size and fulfilment don't change: orders and prices rely on them. To change one, add a new
          item and deactivate the old.
        </p>
      </section>
      {permissions.includes("staff.manage") ? (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Activity</h2>
          <ActivityPanel targetType="product" id={p.id} label="What changed on this product" />
        </section>
      ) : null}
      {dialog?.kind === "edit" ? <EditProduct product={p} onClose={close} /> : null}
      {dialog?.kind === "active" ? (
        <ConfirmDialog
          title={p.is_active ? `Deactivate ${p.name}?` : `Put ${p.name} back on sale?`}
          description={
            p.is_active
              ? "Customers can't order its items any more. Orders already placed go on, and its prices are kept."
              : "Its active items can be ordered again, at their prices."
          }
          action={p.is_active ? "Deactivate" : "Put back on sale"}
          danger={p.is_active}
          run={() => productActions.update(p.id, { is_active: !p.is_active })}
          onDone={refresh}
          onClose={close}
        />
      ) : null}
      {dialog?.kind === "add-item" ? <AddItem product={p} onClose={close} /> : null}
      {dialog?.kind === "edit-item" ? <EditItem product={p} item={dialog.item} onClose={close} /> : null}
      {dialog?.kind === "item-active" ? (
        <ConfirmDialog
          title={dialog.item.is_active ? `Deactivate ${dialog.item.name}?` : `Put ${dialog.item.name} back on sale?`}
          description={
            dialog.item.is_active
              ? "Customers can't order it any more. Orders already placed go on."
              : "Customers can order it again, at its price."
          }
          action={dialog.item.is_active ? "Deactivate" : "Put back on sale"}
          danger={dialog.item.is_active}
          run={() => productActions.updateItem(p.id, dialog.item.id, { is_active: !dialog.item.is_active })}
          onDone={refresh}
          onClose={close}
        />
      ) : null}
    </>
  );
}

function EditProduct({ product, onClose }: { product: Product; onClose: () => void }) {
  const refresh = useRefreshProduct(product.id);
  const [name, setName] = useState(product.name);
  const [description, setDescription] = useState(product.description ?? "");
  const [categoryId, setCategoryId] = useState<string | null>(product.category?.id ?? null);
  const save = useMutation({
    mutationFn: () =>
      productActions.update(product.id, {
        name: name.trim(),
        description: description.trim() || null,
        category_id: categoryId,
      }),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={`Edit ${product.name}`} description="Its type stays as it is.">
        <div className={styles.form}>
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <Textarea
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
          <CategorySelect value={categoryId} onChange={setCategoryId} />
          {save.error ? (
            <Alert tone="danger" title="Not saved">
              {productError(save.error)}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!name.trim()} loading={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddItem({ product, onClose }: { product: Product; onClose: () => void }) {
  const refresh = useRefreshProduct(product.id);
  const [item, setItem] = useState(() => blankItem(product.type === "water"));
  const check = useFormCheck();
  const add = useMutation({
    mutationFn: () => productActions.addItem(product.id, toNewItem(item)),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={`Add an item to ${product.name}`} description="Set its price under Pricing.">
        <div className={styles.form}>
          <ItemFields item={item} onChange={setItem} show={check.show} />
          {add.error ? (
            <Alert tone="danger" title="Not added">
              {productError(add.error)}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button loading={add.isPending} onClick={() => check.submit(itemErrors(item), () => add.mutate())}>
              Add item
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditItem({ product, item, onClose }: { product: Product; item: ProductItem; onClose: () => void }) {
  const refresh = useRefreshProduct(product.id);
  const [name, setName] = useState(item.name);
  const [sku, setSku] = useState(item.sku);
  const save = useMutation({
    mutationFn: () => productActions.updateItem(product.id, item.id, { name: name.trim(), sku: sku.trim() }),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={`Edit ${item.name}`}
        description="Its unit, size and fulfilment stay: add a new item to change them."
      >
        <div className={styles.form}>
          <Input label="Item name" value={name} onChange={(e) => setName(e.target.value)} />
          <Input label="SKU" value={sku} onChange={(e) => setSku(e.target.value)} />
          {save.error ? (
            <Alert tone="danger" title="Not saved">
              {productError(save.error)}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!name.trim() || !sku.trim()} loading={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
